/**
 * PERCURSO DA EMPRESA — o que o cartão do Resumo mostra (pedido 19/10: "os bots
 * que a empresa percorreu, que não percorreu mas tem que percorrer, o que está
 * com erro ou warning").
 *
 * "Percorreu" é o que já vem em `empresa.bots` (o /resumo só traz bot com log).
 * "Tem que percorrer" não vem no /resumo: sai da COBERTURA — a empresa está no
 * `universo_empresas` de uma área, então todo bot daquela área é esperado para
 * ela. A falta é a diferença entre os dois.
 *
 * Duas regras que esta função NÃO inventa, ela herda:
 *   - regime próprio do bot (aba Dependências): bot com regime definido e
 *     diferente do da empresa não é esperado dela — o mesmo recorte que o trilho
 *     do Resumo já aplica (`aplicarRegimeProprioNoResumo`);
 *   - `skip` (pendente) não é conclusão: conta como "não percorreu" no
 *     progresso, igual ao painel do Processos ("pendente conta como não consta").
 */

import type { IAreaCobertura, IBotDaEmpresa, StatusEsteira } from '../api';
import { empresaBateRegime } from './regimeEfetivo';

export interface PercursoDaEmpresa {
  concluidos: IBotDaEmpresa[];
  erros: IBotDaEmpresa[];
  avisos: IBotDaEmpresa[];
  andamento: IBotDaEmpresa[];
  pendentes: IBotDaEmpresa[];
  /** Bots esperados (pela cobertura) que não têm log nenhum desta empresa. */
  faltam: Array<{ bot: string; area: string }>;
  /** Total esperado = percorridos + faltam. */
  total: number;
  /** Concluídos / total, 0–100; null sem nada esperado. */
  pctConcluido: number | null;
}

const GRUPO_DO_STATUS: Record<
  StatusEsteira,
  keyof Omit<PercursoDaEmpresa, 'faltam' | 'total' | 'pctConcluido'>
> = {
  done: 'concluidos',
  err: 'erros',
  warn: 'avisos',
  run: 'andamento',
  wait: 'andamento',
  skip: 'pendentes',
};

/**
 * Índice codi_emp -> bots esperados (com a área de onde vêm). Montado uma vez
 * por resposta da cobertura; o cartão só consulta.
 */
export const indiceDeBotsEsperados = (
  areas: IAreaCobertura[] | undefined,
): Map<string, Array<{ bot: string; area: string }>> => {
  const indice = new Map<string, Array<{ bot: string; area: string }>>();
  (areas ?? []).forEach(area => {
    // Grupo que itera ITEM e não empresa (tickets de "Solicitações Clientes DP",
    // `cobertura_itens` preenchido): os bots dele nunca passam por empresa
    // nenhuma, então cobrá-los de cada empresa da área seria falta falsa.
    const deItens = new Set(
      (area.grupos ?? []).filter(g => g.cobertura_itens).flatMap(g => g.bots ?? []),
    );
    const bots = (area.bots ?? []).filter(bot => !deItens.has(bot));
    if (!bots.length) return;
    (area.universo_empresas ?? []).forEach(codi => {
      const lista = indice.get(codi) ?? [];
      bots.forEach(bot => {
        if (!lista.some(x => x.bot === bot)) lista.push({ bot, area: area.nome });
      });
      indice.set(codi, lista);
    });
  });
  return indice;
};

export const percursoDaEmpresa = (
  bots: IBotDaEmpresa[],
  esperados: Array<{ bot: string; area: string }> | undefined,
  regimeDaEmpresa: string | null,
  regimeProprioDoBot: Map<string, string | null>,
  /** Bots que a fonte escondeu pelo regime: não são "falta", só não aparecem. */
  ocultos: Set<string> = new Set(),
): PercursoDaEmpresa => {
  const p: PercursoDaEmpresa = {
    concluidos: [],
    erros: [],
    avisos: [],
    andamento: [],
    pendentes: [],
    faltam: [],
    total: 0,
    pctConcluido: null,
  };
  bots.forEach(b => {
    p[GRUPO_DO_STATUS[b.status] ?? 'andamento'].push(b);
  });
  const presentes = new Set(bots.map(b => b.bot));
  (esperados ?? []).forEach(x => {
    if (presentes.has(x.bot) || ocultos.has(x.bot)) return;
    // Mesma regra do recorte do trilho (fail-closed): empresa sem regime não
    // é cobrada de bot que tem regime próprio.
    if (!empresaBateRegime({ regime: regimeDaEmpresa }, regimeProprioDoBot.get(x.bot) ?? null))
      return;
    p.faltam.push(x);
  });
  p.total = bots.length + p.faltam.length;
  p.pctConcluido = p.total ? (p.concluidos.length / p.total) * 100 : null;
  return p;
};

// ── Por MÓDULO (19/10) ──────────────────────────────────────────────────────
//
// > "a empresa do Simples Nacional só passa em Fiscal Simples Nacional, Fluxo
// > Municipal, Contábil … separar por módulos: os bots que faltam no total, só
// > na parte do Simples, só na do Municipal, e dá pra escolher mais de um."
//
// Módulo = ÁREA da cobertura (o mesmo "fluxo" que é linha no Processos). Cada
// bot da empresa cai no módulo que o ESPERA dela; sem expectativa, na primeira
// área que o lista; sem área nenhuma, em "Outros".

/** Nome do grupo dos bots que não pertencem a área nenhuma da cobertura. */
export const MODULO_OUTROS = 'Outros';

/** Bot -> primeira área que o lista (ordem da cobertura). */
export const areaDeCadaBot = (areas: IAreaCobertura[] | undefined): Map<string, string> => {
  const mapa = new Map<string, string>();
  (areas ?? []).forEach(a => (a.bots ?? []).forEach(b => !mapa.has(b) && mapa.set(b, a.nome)));
  return mapa;
};

export interface PercursoDoModulo {
  modulo: string;
  percurso: PercursoDaEmpresa;
}

export interface PercursoPorModulo {
  /** Só os módulos de que a empresa participa (e que estão no recorte). */
  modulos: PercursoDoModulo[];
  /** Soma dos módulos acima — é o que o anel e o "falta" do cartão mostram. */
  total: PercursoDaEmpresa;
}

export const percursoPorModulo = (
  bots: IBotDaEmpresa[],
  esperados: Array<{ bot: string; area: string }> | undefined,
  areaDoBot: Map<string, string>,
  ordemModulos: string[],
  regimeDaEmpresa: string | null,
  regimeProprioDoBot: Map<string, string | null>,
  /** Módulos escolhidos no filtro; vazio = todos (inclusive "Outros"). */
  selecionados: string[] = [],
): PercursoPorModulo => {
  const esperadoEm = new Map((esperados ?? []).map(x => [x.bot, x.area]));
  const moduloDe = (bot: string) => esperadoEm.get(bot) ?? areaDoBot.get(bot) ?? MODULO_OUTROS;
  const entra = (m: string) => !selecionados.length || selecionados.includes(m);

  const botsPorModulo = new Map<string, IBotDaEmpresa[]>();
  bots.forEach(b => {
    const m = moduloDe(b.bot);
    if (!entra(m)) return;
    botsPorModulo.set(m, [...(botsPorModulo.get(m) ?? []), b]);
  });
  const esperadosPorModulo = new Map<string, Array<{ bot: string; area: string }>>();
  (esperados ?? []).forEach(x => {
    if (!entra(x.area)) return;
    esperadosPorModulo.set(x.area, [...(esperadosPorModulo.get(x.area) ?? []), x]);
  });

  const nomes = new Set([
    ...Array.from(botsPorModulo.keys()),
    ...Array.from(esperadosPorModulo.keys()),
  ]);
  const posicao = (m: string) => {
    if (m === MODULO_OUTROS) return Number.MAX_SAFE_INTEGER;
    const i = ordemModulos.indexOf(m);
    return i < 0 ? Number.MAX_SAFE_INTEGER - 1 : i;
  };
  const modulos = Array.from(nomes)
    .sort((a, b) => posicao(a) - posicao(b) || a.localeCompare(b, 'pt-BR'))
    .map(modulo => ({
      modulo,
      percurso: percursoDaEmpresa(
        botsPorModulo.get(modulo) ?? [],
        esperadosPorModulo.get(modulo),
        regimeDaEmpresa,
        regimeProprioDoBot,
      ),
    }))
    // Módulo cujo único esperado era de outro regime fica vazio: some.
    .filter(m => m.percurso.total > 0);

  const total = percursoDaEmpresa(
    modulos.flatMap(m => [
      ...m.percurso.concluidos,
      ...m.percurso.erros,
      ...m.percurso.avisos,
      ...m.percurso.andamento,
      ...m.percurso.pendentes,
    ]),
    modulos.flatMap(m => m.percurso.faltam),
    regimeDaEmpresa,
    regimeProprioDoBot,
  );
  return { modulos, total };
};
